import * as t from 'io-ts';
import {isNull, noop} from 'lodash';
import React, {ComponentType, createContext, FC, useCallback, useContext, useEffect, useState} from 'react';
import {Subject} from 'rxjs';
import {Subtract} from 'utility-types';

import {usePrevious} from '../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf, Unsubscriber} from '../../libs/shared/helpers/src/react/reactHelpers';
import {readApiModel} from '../../src/helpers/apiHelpers';
import {ApiType, ModelType} from '../../src/models/api/apiTypes';

/*
 * Props.
 */

export type PushEventSubscriber = (eventName: string, eventData: unknown) => void;

export interface PushProps {
  isConnected: boolean;
  connectionKey: number;
  connect: () => void;
  subscribeToEvents: (subscriber: PushEventSubscriber) => Unsubscriber;
  emitDirectEvent: (roomName: string, eventName: string, data: any) => void;
  roomSubscribes$?: Subject<string>;
}

/*
 * Context.
 */

export const PushContext = createContext<PushProps>({
  isConnected: true,
  connectionKey: 0,
  connect: () => {},
  subscribeToEvents: () => noop,
  emitDirectEvent: noop,
});

/*
 * Hook.
 */

export function usePush() {
  return useContext(PushContext);
}

export function useReconnectHandler(onReconnect: Handler) {
  const {connectionKey} = usePush();
  const previousConnectionKey = usePrevious(connectionKey);

  useEffect(() => {
    if (isNull(previousConnectionKey) || connectionKey === previousConnectionKey) {
      return;
    }

    onReconnect();
  });
}

export function useRequiresConnectionToggle(): [boolean, Handler] {
  const [value, setValue] = useState(false);
  const {isConnected} = usePush();
  const toggle = useCallback(() => setValue((currentValue) => isConnected && !currentValue), []);

  useEffect(() => {
    if (isConnected) {
      return;
    }

    setValue(false);
  }, [isConnected]);

  return [value, toggle];
}

/* eslint react-hooks/exhaustive-deps: 0 */
export function usePushEvents(pushEventHandler: PushEventSubscriber, dependencies: ReadonlyArray<unknown>) {
  const {subscribeToEvents} = usePush();
  useEffect(() => subscribeToEvents(pushEventHandler), [...dependencies, subscribeToEvents]);
}

export function usePushEvent<T extends t.Mixed>(
  eventName: string,
  apiType: ApiType<T>,
  handler: HandlerOf<ModelType<T>>,
) {
  return usePushEvents(
    (name, data) => {
      if (name !== eventName) {
        return;
      }

      handler(readApiModel(apiType, data));
    },
    [apiType, handler],
  );
}

/*
 * HOC.
 */

/* eslint-disable react/jsx-props-no-spreading */
export function withPush<T extends PushProps>(Component: ComponentType<T>): FC<Subtract<T, PushProps>> {
  return function (props) {
    return (
      <PushContext.Consumer>
        {(context) => <Component {...(props as any)} {...context} />}
      </PushContext.Consumer>
    );
  };
}
/* eslint-enable react/jsx-props-no-spreading */
